import { createReducer, on, Action } from '@ngrx/store';
import { ApiError, FareRequest, FareSelectorList } from '../../client/src';
import { FareSelectorActions } from '../../actions';
import { LoadState } from '@nx-ng/types';
import { IFareSelectorsBoolean, initialFareSelectorState } from '../../types';
import { highlightInvalidSelections } from './util/highlight-invalid-selections';

export interface IFareSelectorsState {
  fareSelectorList?: FareSelectorList;
  loaded: LoadState;
  error?: ApiError | string; // last known error (if any)
  selections: Partial<FareRequest>;
  invalidSelections: IFareSelectorsBoolean;
  touched: IFareSelectorsBoolean;
}

export const initialState: IFareSelectorsState = {
  // set initial required properties
  loaded: LoadState.NOT_LOADED,
  selections: {
    customerType: undefined,
    zone: undefined,
    travelMethod: undefined,
    travelTime: undefined,
  },
  invalidSelections: {
    ...initialFareSelectorState,
  },
  touched: {
    ...initialFareSelectorState,
  },
};

const fareSelectorsReducer = createReducer(
  initialState,
  on(FareSelectorActions.loadTicketSelectors, (state) => ({
    ...state,
    loaded: LoadState.LOADING,
    error: undefined,
    invalidSelections: {
      ...initialFareSelectorState,
    },
  })),
  on(
    FareSelectorActions.loadTicketSelectorsSuccess,
    (state, { fareSelectorList }: { fareSelectorList: FareSelectorList }) => ({
      ...state,
      fareSelectorList,
      invalidSelections: highlightInvalidSelections({
        invalidSelections: state.invalidSelections,
        currentSelections: state.selections,
        fareSelectorList,
      }),
      loaded: LoadState.LOADED,
    })
  ),
  on(FareSelectorActions.loadTicketSelectorsFailure, (state, { error }) => ({
    ...state,
    error,
    loaded: LoadState.FAILED,
  })),
  on(FareSelectorActions.setCustomerType, (state, { customerType }) => ({
    ...state,
    selections: {
      ...state.selections,
      customerType,
    },
    touched: {
      ...state.touched,
      customerType: true,
    },
  })),
  on(FareSelectorActions.setZone, (state, { zone }) => ({
    ...state,
    selections: {
      ...state.selections,
      zone,
    },
    touched: {
      ...state.touched,
      zone: true,
    },
    invalidSelections: {
      ...state.invalidSelections,
      zone: false,
    },
  })),
  on(FareSelectorActions.setTravelMethod, (state, { travelMethod }) => ({
    ...state,
    selections: {
      ...state.selections,
      travelMethod,
    },
    touched: {
      ...state.touched,
      travelMethod: true,
    },
    invalidSelections: {
      ...state.invalidSelections,
      travelMethod: false,
    },
  })),
  on(FareSelectorActions.setTravelTime, (state, { travelTime }) => ({
    ...state,
    selections: {
      ...state.selections,
      travelTime,
    },
    touched: {
      ...state.touched,
      travelTime: true,
    },
    invalidSelections: {
      ...state.invalidSelections,
      travelTime: false,
    },
  })),
  on(FareSelectorActions.setCustomerTypeTouched, (state) => ({
    ...state,
    touched: {
      ...state.touched,
      customerType: true,
    },
  })),
  on(FareSelectorActions.setZoneTouched, (state) => ({
    ...state,
    touched: {
      ...state.touched,
      zone: true,
    },
  })),
  on(FareSelectorActions.setTravelMethodTouched, (state) => ({
    ...state,
    touched: {
      ...state.touched,
      travelMethod: true,
    },
  })),
  on(FareSelectorActions.setTravelTimeTouched, (state) => ({
    ...state,
    touched: {
      ...state.touched,
      travelTime: true,
    },
  }))
);

export function reducer(
  state: IFareSelectorsState | undefined,
  action: Action
) {
  return fareSelectorsReducer(state, action);
}
